import React, { useState, useRef, useEffect } from "react";
import { FaRegThumbsUp, FaRegThumbsDown } from "react-icons/fa6";

const IconButton = ({ onClick, title, active = false, children, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-1.5 rounded-lg transition-colors ${className}`}
  >
    {children}
  </button>
);

const DropdownItem = ({ onClick, children, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-full text-left px-3 py-2 text-sm rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-2"
  >
    {children}
  </button>
);

const MessageActions = ({
  message,
  isDarkMode,
  isAssistant = false,
  currentPlan,
  user,
  onCopy,
  onEdit,
  onRegenerate,
  onThumbsUp,
  onThumbsDown,
  thumbsState = {}, // { up: boolean, down: boolean }
  onAskAdmin,
  onSchedule,
  onGeneratePDF,
  onGeneratePPT,
  onGenerateMarketingStrategy,
  onDownloadReport,
  strategyForms = {},
  marketingForms = {},
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const contentLength = message.content ? message.content.split(/\s+/).length : 0;
  const isLongMessage = contentLength > 100;
  const hasMedia = message.imageUrl || message.videoUrl;
  const isCMO = currentPlan?.includes("CMO");
  const isFree = currentPlan?.includes("free");

  return (
    <div className="flex items-center gap-0.5 mt-0.5 px-1">
      {/* Copy — always visible */}
      <IconButton
        onClick={onCopy}
        title="Copy message"
        className={isDarkMode
          ? "text-gray-400 hover:text-white hover:bg-gray-700"
          : "text-gray-500 hover:text-gray-900 hover:bg-gray-200"
        }
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a-2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </IconButton>

      {/* Edit — only on user messages */}
      {!isAssistant && onEdit && (
        <IconButton
          onClick={onEdit}
          title="Edit message"
          className={isDarkMode
            ? "text-gray-400 hover:text-white hover:bg-gray-700"
            : "text-gray-500 hover:text-gray-900 hover:bg-gray-200"
          }
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </IconButton>
      )}
      {isAssistant && onRegenerate && (
        <IconButton
          onClick={onRegenerate}
          title="Regenerate response"
          className={isDarkMode
            ? "text-gray-400 hover:text-white hover:bg-gray-700"
            : "text-gray-500 hover:text-gray-900 hover:bg-gray-200"
          }
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </IconButton>
      )}

      {/* More actions dropdown */}
      <div className="relative" ref={dropdownRef}>
        <IconButton
          onClick={() => setDropdownOpen((prev) => !prev)}
          title="More actions"
          className={isDarkMode
            ? "text-gray-400 hover:text-white hover:bg-gray-700"
            : "text-gray-500 hover:text-gray-900 hover:bg-gray-200"
          }
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
          </svg>
        </IconButton>

        {dropdownOpen && (
          <div
            className={`absolute left-0 bottom-full mb-1 z-20 min-w-[180px] rounded-lg shadow-lg border py-1 ${
              isDarkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            {/* Thumbs Up */}
            <DropdownItem onClick={() => { onThumbsUp?.(); setDropdownOpen(false); }}>
              <FaRegThumbsUp className={`w-3.5 h-3.5 ${thumbsState.up ? "text-green-500 fill-current" : ""}`} />
              <span className={thumbsState.up ? "font-medium" : ""}>Helpful</span>
            </DropdownItem>

            {/* Thumbs Down */}
            <DropdownItem onClick={() => { onThumbsDown?.(); setDropdownOpen(false); }}>
              <FaRegThumbsDown className={`w-3.5 h-3.5 ${thumbsState.down ? "text-red-500 fill-current" : ""}`} />
              <span className={thumbsState.down ? "font-medium" : ""}>Not helpful</span>
            </DropdownItem>

            {/* Ask Admin */}
            {onAskAdmin && (
              <DropdownItem onClick={() => { onAskAdmin(); setDropdownOpen(false); }}>
                <img
                  src="/assets/ask-admin-icon.png"
                  alt=""
                  className={`w-4 h-4 ${isDarkMode ? "invert opacity-80" : "opacity-70"}`}
                />
                Ask Admin
              </DropdownItem>
            )}

            {/* Schedule to Social */}
            {hasMedia && user?.currentPlan === "CMO" && onSchedule && (
              <DropdownItem onClick={() => { onSchedule(); setDropdownOpen(false); }}>
                <span>📅</span>
                Schedule to Social
              </DropdownItem>
            )}

            {/* Generate PDF */}
            {isLongMessage && onGeneratePDF && (
              <DropdownItem onClick={() => { onGeneratePDF(); setDropdownOpen(false); }}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Generate PDF
                {isFree && (
                  <svg className="w-3 h-3 ml-auto text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                )}
              </DropdownItem>
            )}

            {/* Generate PPT */}
            {isLongMessage && onGeneratePPT && (
              <DropdownItem onClick={() => { onGeneratePPT(); setDropdownOpen(false); }}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 13v-1m4 1v-3m4 3V8M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
                Generate PPT
                {!isCMO && (
                  <svg className="w-3 h-3 ml-auto text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                )}
              </DropdownItem>
            )}

            {/* Download Full Report */}
            {isLongMessage && onDownloadReport && (
              <DropdownItem onClick={() => { onDownloadReport(); setDropdownOpen(false); }}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3" />
                </svg>
                Download Full Report
              </DropdownItem>
            )}

            {/* Marketing Strategy */}
            {isCMO && isLongMessage && onGenerateMarketingStrategy && (
              <DropdownItem
                onClick={() => { onGenerateMarketingStrategy(); setDropdownOpen(false); }}
                disabled={strategyForms[message.id]?.open || strategyForms[message.id]?.submitted || marketingForms[message.id]?.open || marketingForms[message.id]?.submitted}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
                Marketing Strategy
              </DropdownItem>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageActions;
